<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="images">
					<app-heading :title="`${website.name} Images`">
						<template v-slot:left>
							<app-button v-if="$ravello.can('website.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								small
								outlined
								:to="{ name: 'website-image', params: { image: 'new' } }"
							>Add Image</app-button>
						</template>
						<template v-slot:right>
							<app-button v-if="$ravello.can('website.post') && !website.template"
								color="secondary"
								small
								outlined
								@click="onPublish"
							>Publish</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="images"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								@update="fetchImages"
							>
								<template v-slot:url="{ item }">
									<a :href="item.url" target="_blank">
										<v-img :src="item.url" width="64" height="64" class="ma-2" />
									</a>
								</template>
								<template v-slot:code="{ item }">
									<app-button
										flat
										small
										class="ma-0 text-lowercase"
										icon="far fa-copy"
										icon-right
										@click="onCodeClick(getCode(item))"
									>{{ getCode(item) }}</app-button>
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										v-if="$ravello.can('website.put')"
										color="secondary"
										:to="{ name: 'website-image', params: { image: item._id } }"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('website.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove image <b>${item.filename}</b>?`"
										title="Remove Image"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'website-image', params: { image: 'new' } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { websiteContentTabs } from '@/datasources/tabs'

export default {
	data() {
		return {
			website: {},
			images: [],
			namespacesMap: {},
			count: -1,
			refresh: false,
			tabs: [],
			headers: [{
				text: '',
				value: 'url',
				sortable: false,
			}, {
				text: 'Code',
				value: 'code',
				sortable: false,
			}, {
				text: 'File name',
				value: 'filename',
				sortable: true,
			}, {
				text: 'Namespace',
				value: 'namespace.name',
				sortable: true,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
	},
	async mounted() {
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.fields(['name', 'template'])
			.query()

		this.tabs = websiteContentTabs(this.websiteId)
	},
	methods: {
		getCode(item) {
			if (!item.namespace) {
				return 'Missing namespace'
			}

			const { slug: namespaceSlug } = item.namespace
			return `${namespaceSlug}/${item.filename}`
		},

		async onCodeClick(code) {
			try {
				await this.$copyText(code)
				this.$notifications.pushSnackbar('Image code copied to clipboard!')
			} catch (err) {
				this.$notifications.pushAlert('error', err.message)
			}
		},

		async fetchImages(pagination) {
			const request = this.$ravello.graphql('images')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.fields(['_id', 'url', 'filename'])
				.include(this.$ravello.graphql('namespace').fields(['slug', 'name']))
				.fromPagination(pagination)

			if (this.count === -1) {
				request.count('count')
			}
			try {
				this.images = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('images')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},

		async onPublish() {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('images')
					.to('build')
					.create()
				this.$notifications.pushAlert('success', 'Images published!')
			} catch (err) {
				this.$notifications.pushAlert('error', err.data ? err.data.message : err.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
